import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { useUserOrg } from '../lib/org';
import { listArchivedSites, restoreSite, type Site } from '../lib/db';
import { useConfirm } from '../components/ConfirmProvider';
import { LoadingIndicator } from '../components/LoadingIndicator';

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function CompletedJobs() {
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    if (!org) return;
    setLoading(true);
    try {
      setSites(await listArchivedSites(org.org_id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); /* eslint-disable-next-line */ }, [org]);

  async function onRestore(s: Site) {
    if (!(await confirm({
      title: `Restore "${s.name}"?`,
      body: 'It will move back to your active sites.',
      confirmLabel: 'Restore',
    }))) return;
    try {
      await restoreSite(s.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppShell title="Completed jobs" back="/">
      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: 13 }}>
          Sites you've finished. All H&S records (RAMS, sign-ins, accidents, permits) stay attached
          for audit purposes — typically 7 years for sign-ins / inspections, longer for accident records.
          You can re-open a job any time if work resumes.
        </p>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {loading ? (
        <LoadingIndicator />
      ) : sites.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'var(--ink-soft)' }}>
            No completed jobs yet. When you finish a site, archive it from the Sites page and it'll show up here.
          </p>
        </div>
      ) : (
        <ul className="home-site-list">
          {sites.map((s) => (
            <li key={s.id}>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>{s.name}</p>
                  <span style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
                    archived {fmt(s.archived_at)}
                  </span>
                </div>
                {s.address && (
                  <p style={{ margin: '4px 0 8px', fontSize: 12, color: 'var(--ink-soft)' }}>{s.address}</p>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <Link
                    to={`/sites/${s.id}/records`}
                    className="btn btn-ghost"
                    style={{ padding: '4px 10px', fontSize: 12, textDecoration: 'none' }}
                  >
                    View records
                  </Link>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => onRestore(s)}
                  >
                    Re-open job
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </AppShell>
  );
}
